import React, { type ReactElement, useEffect, useMemo, useState, useCallback } from 'react';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { useGetCurrentUrl } from '@jira/navigation-apps__routing-hooks/src/useGetCurrentUrl.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import { useEntryPoint } from '@jira/platform__entry-point/src/controllers/use-entry-point/index.tsx';
import { SECTION_ITEM_PROJECTS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_PROJECTS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import {
	L1_MENU_ID,
	type LeftSidebarEntryPointConfig,
	type Nav4DragAndDropMenuItemProps,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { Nav4ExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { SkeletonNavMenuItems } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/skeleton-nav-menu-items/main.tsx';
import { getTestId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { useSectionItemAnalytics } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-section-item-analytics/index.tsx';
import { useConfigurableMessageAndIconMap } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import { useExpandableEntryPointTrigger } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-expandable-entry-point-trigger/index.tsx';
import { usePageLayoutEntryPoint } from '@jira/platform__entry-points-page-layout-entry-point/src/index.tsx';
import { LEFT_SIDEBAR } from '@jira/platform__entry-points-page-layout-fields/src/index.tsx';
import { RouteEntryPointContainer } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/route-entry-point-adapter/index.tsx';
// eslint-disable-next-line jira/import/file-extension-required
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component';
import {
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import { mergeRefs } from '@atlassian/jira-merge-refs';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../../../common/constants.tsx';
import { nav4ProjectsContentEntryPoint } from './async-content/nav4ProjectsContentEntryPoint.entrypoint.tsx';

type Nav4ProjectsDefaultProps = Nav4DragAndDropMenuItemProps & {
	actionsOnHover: ReactElement;
	isExpanded: boolean;
	simulateLoading?: boolean; // For testing only
};

export const Nav4ProjectsDefault: React.FC<Nav4ProjectsDefaultProps> = componentWithFG(
	'navx-global-side-nav',
	Nav4ProjectsDefaultGSN,
	Nav4ProjectsDefaultNav4,
);

function Nav4ProjectsDefaultGSN(props: Nav4ProjectsDefaultProps) {
	const {
		actionsOnHover,
		draggableRef,
		dropIndicator,
		isDragging,
		isExpanded,
		hasDragIndicator,
		simulateLoading = false,
		onExpandFlyoutToggle,
		visualContentRef,
	} = props;
	const cloudId = useCloudId();
	const activationId = useActivationId();
	const atlassianAccountId = useAccountId();
	const currentURL = useGetCurrentUrl();
	const entryPointParamsOld = useMemo(
		() => ({ cloudId, currentURL, activationId, atlassianAccountId }),
		[cloudId, currentURL, activationId, atlassianAccountId],
	);

	// oxlint-disable-next-line typescript/consistent-type-assertions
	const entryPointParams = useMemo(
		() => ({ tenantContext: { cloudId, activationId, atlassianAccountId } }),
		[cloudId, activationId, atlassianAccountId],
	) as EntryPointRouteParams;

	// oxlint-disable-next-line typescript/consistent-type-assertions
	const leftSidebarEPRef = usePageLayoutEntryPoint(LEFT_SIDEBAR) as LeftSidebarEntryPointConfig;
	const projectsEPRef = leftSidebarEPRef?.entryPoints?.projects;

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		nav4ProjectsContentEntryPoint,
		entryPointParams,
	);

	const triggerRef = useExpandableEntryPointTrigger(entryPointActions);
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.PROJECTS);
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();

	// If starting life expanded, make sure to load.
	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		// We intentionally won't re-evaluate this if the route changes.
		if (!projectsEPRef && isExpanded) {
			entryPointActions.load();
		}
	}, [entryPointActions, projectsEPRef, isExpanded]);

	const menuId = MENU_ID_PROJECTS;

	const skeleton = useMemo(
		() => (
			<>
				<SkeletonNavMenuItems count={6} withHeading />
				<SkeletonNavMenuItems count={6} withHeading />
			</>
		),
		[],
	);

	// Create this component once, then keep using it FOREVER.
	// We don't want to keep switching the implementation as the user does SPA transitions.
	// We set the state this to cater for components that may be an entrypoint so that we don't keep switching between the entryPoint version and the non-entryPoint version
	const [entryPointConfig] = useState(() => {
		const epContainerProps = {
			id: 'nav4-sidebar-projects-default',
			packageName: PACKAGE_NAME,
			teamName: TEAM_NAME,
			errorFallback: 'flag' as const,
			fallback: skeleton,
			// oxlint-disable-next-line typescript/consistent-type-assertions
			runtimeProps: entryPointParamsOld as never,
		};

		return {
			Component: () => (
				<RouteEntryPointContainer
					epContainerProps={epContainerProps}
					jiraEPRef={entryPointReferenceSubject}
					nestedEPRef={projectsEPRef}
					placeholderEntryPoint={nav4ProjectsContentEntryPoint}
				/>
			),
		};
	});

	const content = () => <entryPointConfig.Component />;

	const handleSystemToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				entryPointActions.load();
			}
		},
		[entryPointActions],
	);

	const handleExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				entryPointActions.load();

				sendSectionItemAnalyticsEvent({
					menuId: L1_MENU_ID.PROJECTS,
					sectionItem: SECTION_ITEM_PROJECTS,
				});
			}
			onExpandFlyoutToggle?.(hasExpanded);
		},
		[entryPointActions, onExpandFlyoutToggle, sendSectionItemAnalyticsEvent],
	);

	return (
		<Nav4ExpandableMenuItem
			dropTargetRef={visualContentRef}
			dropIndicator={dropIndicator}
			isDragging={isDragging}
			isExpanded={isExpanded}
			menuId={menuId}
			onSystemToggle={handleSystemToggle}
			onExpansionToggle={handleExpansionToggle}
		>
			<ExpandableMenuItemTrigger
				actionsOnHover={actionsOnHover}
				aria-label={formattedMessage}
				elemBefore={icon}
				hasDragIndicator={hasDragIndicator}
				interactionName="nav4-sidebar-projects-default"
				isDragging={isDragging}
				ref={draggableRef ? mergeRefs(triggerRef, draggableRef) : triggerRef}
				testId={getTestId(menuId)}
			>
				{formattedMessage}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				{simulateLoading ? skeleton : content()}
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}

function Nav4ProjectsDefaultNav4(props: Nav4ProjectsDefaultProps) {
	const { actionsOnHover, isExpanded, simulateLoading = false } = props;
	const cloudId = useCloudId();
	const activationId = useActivationId();
	const atlassianAccountId = useAccountId();
	const currentURL = useGetCurrentUrl();
	const entryPointParamsOld = useMemo(
		() => ({ cloudId, currentURL, activationId, atlassianAccountId }),
		[cloudId, currentURL, activationId, atlassianAccountId],
	);

	// oxlint-disable-next-line typescript/consistent-type-assertions
	const entryPointParams = useMemo(
		() => ({ tenantContext: { cloudId, activationId, atlassianAccountId } }),
		[cloudId, activationId, atlassianAccountId],
	) as EntryPointRouteParams;

	// oxlint-disable-next-line typescript/consistent-type-assertions
	const leftSidebarEPRef = usePageLayoutEntryPoint(LEFT_SIDEBAR) as LeftSidebarEntryPointConfig;
	const projectsEPRef = leftSidebarEPRef?.entryPoints?.projects;

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		nav4ProjectsContentEntryPoint,
		entryPointParams,
	);

	const triggerRef = useExpandableEntryPointTrigger(entryPointActions);
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.PROJECTS);
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();

	// If starting life expanded, make sure to load.
	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		// We intentionally won't re-evaluate this if the route changes.
		if (!projectsEPRef && isExpanded) {
			entryPointActions.load();
		}
	}, [entryPointActions, projectsEPRef, isExpanded]);

	const menuId = MENU_ID_PROJECTS;

	const skeleton = useMemo(
		() => (
			<>
				<SkeletonNavMenuItems count={6} withHeading />
				<SkeletonNavMenuItems count={6} withHeading />
			</>
		),
		[],
	);

	// Create this component once, then keep using it FOREVER.
	// We don't want to keep switching the implementation as the user does SPA transitions.
	// We set the state this to cater for components that may be an entrypoint so that we don't keep switching between the entryPoint version and the non-entryPoint version
	const [entryPointConfig] = useState(() => {
		const epContainerProps = {
			id: 'nav4-sidebar-projects-default',
			packageName: PACKAGE_NAME,
			teamName: TEAM_NAME,
			errorFallback: 'flag' as const,
			fallback: skeleton,
			// oxlint-disable-next-line typescript/consistent-type-assertions
			runtimeProps: entryPointParamsOld as never,
		};

		return {
			Component: () => (
				<RouteEntryPointContainer
					epContainerProps={epContainerProps}
					jiraEPRef={entryPointReferenceSubject}
					nestedEPRef={projectsEPRef}
					placeholderEntryPoint={nav4ProjectsContentEntryPoint}
				/>
			),
		};
	});

	const content = () => <entryPointConfig.Component />;

	return (
		<Nav4ExpandableMenuItem
			isExpanded={isExpanded}
			menuId={menuId}
			onSystemToggle={useCallback(
				(hasExpanded: boolean) => {
					if (hasExpanded) {
						entryPointActions.load();
					}
				},
				[entryPointActions],
			)}
			onExpansionToggle={useCallback(
				(hasExpanded: boolean) => {
					if (hasExpanded) {
						entryPointActions.load();

						sendSectionItemAnalyticsEvent({
							menuId: L1_MENU_ID.PROJECTS,
							sectionItem: SECTION_ITEM_PROJECTS,
						});
					}
				},
				[entryPointActions, sendSectionItemAnalyticsEvent],
			)}
		>
			<ExpandableMenuItemTrigger
				interactionName="nav4-sidebar-projects-default"
				actionsOnHover={actionsOnHover}
				// Prevent double-up of accessible name that occurs in icon+text situation.
				aria-label={formattedMessage}
				elemBefore={icon}
				ref={triggerRef}
				testId={getTestId(menuId)}
			>
				{formattedMessage}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				{simulateLoading ? skeleton : content()}
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}
